import React, {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentLayoutAction, Toast } from 'components';
import { SqPendingMethodContext, SqToastContext } from 'context';
import {
  CustomerConfirmStatusDetails,
  OrderStatus,
  OrderStatusView,
} from 'enums';
import {
  useCancelSurveyReasons,
  useGatewayRedirect,
  useOrderContext,
  useToastState,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { useOrderStatusView } from 'pages/Order/hooks/useOrderStatusView';
import { Order, OrderLayoutProps, TradeMethod } from 'types';

import { SqCancelModal, SqCancelFeedback } from '../SqCancelModal';
import { SqCountdownTimer } from '../SqCountdownTimer';
import { SqPaymentLoading } from '../SqPaymentLoading';

type FooterConfig = {
  primary: PaymentLayoutAction & { disabled?: boolean; isLoading?: boolean };
  secondary?: PaymentLayoutAction;
};

const FINAL_STATUSES = [
  OrderStatus.Completed,
  OrderStatus.Cancelled,
  OrderStatus.Dispute,
];

export const SqLayout: React.FC<OrderLayoutProps> = ({
  children,
}: OrderLayoutProps) => {
  const [pendingTradeMethod, setPendingTradeMethod] = useState<
    TradeMethod | undefined
  >();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const { toastOpen, toastText, showToast } = useToastState();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components',
  });

  const { t: tToast } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_toast',
  });

  const showCopiedToast = useCallback(
    (label: string) => {
      if (label) {
        showToast(tToast('copied'));
      }
    },
    [showToast, tToast],
  );

  const {
    order,
    status,
    orderActions: { actions, selectPaymentType, cancelWithFeedback },
    currencySymbol,
    refetchOrderStatus,
    tradeMethodSelection: { startPaymentSyncFailed },
    isStartPaymentSyncInProgress,
    isStartRequisitesCascadeLoading,
    isTSBPSingleBankSelection,
    paymentForm: {
      isCustomerDataConfirmed: cardConfirmed,
      paymentFieldsLoading,
    },
    receiptsResult: { uppy, hasPendingFile, isUploadingReceipt },
  } = useOrderContext();

  const { innerStatus } = useOrderStatusView({ order, status, cardConfirmed });
  const { reasons: cancelSurveyReasons } = useCancelSurveyReasons();
  const { redirectLink } = useGatewayRedirect();

  const handleCancelConfirm = useCallback(
    (feedback: SqCancelFeedback) => {
      setIsCancelModalOpen(false);
      const hasFeedback =
        feedback.reasons.length > 0 || feedback.comment.trim().length > 0;
      cancelWithFeedback(hasFeedback ? feedback : undefined);
    },
    [cancelWithFeedback],
  );

  useEffect(() => {
    if (
      order.statusDetails === CustomerConfirmStatusDetails.CustomerPayed &&
      order.shop?.collectCustomerReceipts
    ) {
      setIsConfirmingPayment(false);
    }
  }, [order.statusDetails, order.shop?.collectCustomerReceipts]);

  useEffect(() => {
    if (isConfirmingPayment && status !== OrderStatus.CustomerConfirm) {
      setIsConfirmingPayment(false);
    }
  }, [isConfirmingPayment, status]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const isNewNormal = useMemo(
    () =>
      status === OrderStatus.New &&
      !startPaymentSyncFailed &&
      !isStartPaymentSyncInProgress &&
      !isStartRequisitesCascadeLoading,
    [
      status,
      startPaymentSyncFailed,
      isStartPaymentSyncInProgress,
      isStartRequisitesCascadeLoading,
    ],
  );

  const isNewFailed = useMemo(
    () =>
      status === OrderStatus.New &&
      startPaymentSyncFailed &&
      !isStartPaymentSyncInProgress &&
      !isStartRequisitesCascadeLoading,
    [
      status,
      startPaymentSyncFailed,
      isStartPaymentSyncInProgress,
      isStartRequisitesCascadeLoading,
    ],
  );

  const isTransfer = useMemo(
    () =>
      innerStatus === OrderStatusView.CustomerConfirmTransfer &&
      !isStartPaymentSyncInProgress,
    [innerStatus, isStartPaymentSyncInProgress],
  );

  const isConfirmCard = useMemo(
    () =>
      innerStatus === OrderStatusView.CustomerConfirmCard &&
      !isStartPaymentSyncInProgress,
    [innerStatus, isStartPaymentSyncInProgress],
  );

  const isReceiptsStep = useMemo(
    () =>
      status === OrderStatus.CustomerConfirm &&
      order.statusDetails === CustomerConfirmStatusDetails.CustomerPayed &&
      !!order.shop?.collectCustomerReceipts,
    [status, order.statusDetails, order.shop?.collectCustomerReceipts],
  );

  const isPaymentLoading = useMemo(
    () =>
      status === OrderStatus.Requisites ||
      paymentFieldsLoading ||
      isStartPaymentSyncInProgress ||
      isStartRequisitesCascadeLoading,
    [
      status,
      paymentFieldsLoading,
      isStartPaymentSyncInProgress,
      isStartRequisitesCascadeLoading,
    ],
  );

  const header = renderHeader({
    isGatewayRedirect: !!redirectLink,
    isNewNormal,
    isTransfer,
    isConfirmCard,
    isReceiptsStep,
    isPaymentLoading,
    order,
    currencySymbol,
    refetchOrderStatus,
    t,
  });

  const submitReceiptAction = useMemo<PaymentLayoutAction>(
    () => ({
      label: t('payment_receipts_dispute.buttons.sq_submit'),
      onClick: () => {
        uppy.upload();
      },
    }),
    [t, uppy],
  );

  const confirmPaymentWithLoading = useCallback(() => {
    setIsConfirmingPayment(true);
    actions.confirmPayment.onClick?.();
  }, [actions.confirmPayment]);

  const footer = renderFooter({
    redirectLink,
    redirectLabel: t('ecom_redirect.submit'),
    isNewNormal,
    isNewFailed,
    isTransfer,
    isConfirmCard,
    isTSBPSingleBankSelection,
    isFinalStatus: FINAL_STATUSES.includes(status),
    isTraderConfirm: status === OrderStatus.TraderConfirm,
    isPaymentLoading,
    hasPendingFile,
    isUploadingReceipt,
    submitReceiptAction,
    pendingTradeMethod,
    selectPaymentType,
    actions,
    confirmPaymentWithLoading,
    continueLabel: t('bank_select.buttons.continue'),
    cancelLabel: t('sq_layout.cancel_payment'),
    backToPersonalAccountLabel: t('sq_layout.back_to_personal_account'),
    isReceiptsStep,
  });

  return (
    <SqToastContext.Provider value={{ showCopiedToast, showToast }}>
      <SqPendingMethodContext.Provider
        value={{ pendingTradeMethod, setPendingTradeMethod }}
      >
        <div
          className="tw-relative tw-flex tw-flex-col tw-flex-1 tw-w-full tw-max-w-[480px] tw-mx-auto tw-overflow-hidden tw-bg-layout sm:tw-rounded-3xl sm:tw-shadow-[0px_0px_30px_0px_rgba(0,0,0,0.14)]"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {toastOpen && <Toast text={toastText} />}

          {isConfirmingPayment ? (
            <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-p-4">
              <SqPaymentLoading status={OrderStatus.TraderConfirm} />
            </div>
          ) : (
            <Fragment>
              {header && (
                <div className="tw-shrink-0 tw-flex tw-flex-col tw-p-4 tw-gap-1">
                  {header}
                </div>
              )}

              <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-p-4">
                {children}
              </div>

              {footer && (
                <div className="tw-shrink-0 tw-flex tw-flex-col tw-px-4 tw-pt-4 tw-pb-4 tw-gap-[6px]">
                  <button
                    data-testid="sq-footer-primary-button"
                    className="tw-w-full tw-h-[54px] tw-rounded-full tw-font-medium tw-text-base tw-border-none tw-cursor-pointer disabled:tw-bg-disabled-button disabled:tw-text-disabled-button disabled:tw-cursor-default enabled:tw-bg-primary-button enabled:tw-text-primary-button tw-flex tw-items-center tw-justify-center"
                    disabled={footer.primary.disabled}
                    onClick={footer.primary.onClick}
                  >
                    {footer.primary.isLoading ? (
                      <span className="tw-inline-block tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent" />
                    ) : (
                      footer.primary.label
                    )}
                  </button>

                  {footer.secondary && !footer.secondary.hidden && (
                    <button
                      data-testid="sq-footer-secondary-button"
                      className="tw-w-full tw-h-[54px] tw-rounded-[24px] tw-bg-transparent tw-text-cancel-action tw-font-medium tw-text-base tw-border-none tw-cursor-pointer"
                      onClick={() => setIsCancelModalOpen(true)}
                    >
                      {footer.secondary.label}
                    </button>
                  )}
                </div>
              )}
            </Fragment>
          )}

          <SqCancelModal
            open={isCancelModalOpen}
            reasons={cancelSurveyReasons}
            onDismiss={() => setIsCancelModalOpen(false)}
            onConfirm={handleCancelConfirm}
          />
        </div>
      </SqPendingMethodContext.Provider>
    </SqToastContext.Provider>
  );
};

type RenderHeaderArgs = {
  isGatewayRedirect: boolean;
  isNewNormal: boolean;
  isTransfer: boolean;
  isConfirmCard: boolean;
  isReceiptsStep: boolean;
  isPaymentLoading: boolean;
  order: Order;
  currencySymbol: string;
  refetchOrderStatus: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
};

function renderHeader({
  isGatewayRedirect,
  isNewNormal,
  isTransfer,
  isConfirmCard,
  isReceiptsStep,
  isPaymentLoading,
  order,
  currencySymbol,
  refetchOrderStatus,
  t,
}: RenderHeaderArgs): ReactNode {
  if (isPaymentLoading || isGatewayRedirect) {
    return null;
  }

  if (isNewNormal) {
    return (
      <>
        <span className="tw-text-sm tw-font-medium tw-text-[#777777]">
          {t('sq_layout.amount_label', {
            amount: order.amount,
            currency: currencySymbol,
          })}
        </span>
        <span className="tw-text-2xl tw-font-semibold tw-text-black tw-tracking-[-0.03em] tw-leading-[1.2]">
          {t('bank_select.buttons.title')}
        </span>
      </>
    );
  }

  if (isReceiptsStep) {
    return (
      <span className="tw-text-2xl tw-font-semibold tw-text-black tw-tracking-[-0.03em] tw-leading-[1.2]">
        {t('sq_layout.upload_receipt_title')}
      </span>
    );
  }

  if (isTransfer) {
    return (
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-[10px]">
        <span className="tw-text-2xl tw-font-semibold tw-text-black tw-tracking-[-0.03em] tw-leading-[1.2]">
          {t('payment_details.transfer_title')}
        </span>
        <SqCountdownTimer
          order={order}
          refetchOrderStatus={refetchOrderStatus}
        />
      </div>
    );
  }

  if (isConfirmCard) {
    return (
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-[10px]">
        <span className="tw-text-2xl tw-font-semibold tw-text-black tw-tracking-[-0.03em] tw-leading-[1.2]">
          {t('payment_title.customer_confirm_card.title')}
        </span>
        <SqCountdownTimer
          order={order}
          refetchOrderStatus={refetchOrderStatus}
        />
      </div>
    );
  }

  return null;
}

type RenderFooterArgs = {
  redirectLink: string | null;
  redirectLabel: string;
  isNewNormal: boolean;
  isNewFailed: boolean;
  isTransfer: boolean;
  isConfirmCard: boolean;
  isTSBPSingleBankSelection: boolean;
  isFinalStatus: boolean;
  isTraderConfirm: boolean;
  isPaymentLoading: boolean;
  hasPendingFile: boolean;
  isUploadingReceipt: boolean;
  submitReceiptAction: PaymentLayoutAction;
  pendingTradeMethod: TradeMethod | undefined;
  selectPaymentType: (tradeMethod: TradeMethod) => void;
  actions: ReturnType<typeof useOrderContext>['orderActions']['actions'];
  confirmPaymentWithLoading: () => void;
  continueLabel: string;
  cancelLabel: string;
  backToPersonalAccountLabel: string;
  isReceiptsStep: boolean;
};

function renderFooter({
  redirectLink,
  redirectLabel,
  isNewNormal,
  isNewFailed,
  isTransfer,
  isConfirmCard,
  isTSBPSingleBankSelection,
  isFinalStatus,
  isTraderConfirm,
  isPaymentLoading,
  hasPendingFile,
  isUploadingReceipt,
  submitReceiptAction,
  pendingTradeMethod,
  selectPaymentType,
  actions,
  confirmPaymentWithLoading,
  continueLabel,
  cancelLabel,
  backToPersonalAccountLabel,
  isReceiptsStep,
}: RenderFooterArgs): FooterConfig | null {
  if (redirectLink) {
    return {
      primary: {
        label: redirectLabel,
        onClick: () => window.location.assign(redirectLink),
      },
    };
  }

  if (isPaymentLoading) {
    return null;
  }

  const cancelAction = { ...actions.cancel, label: cancelLabel };

  if (isNewNormal) {
    return {
      primary: {
        label: continueLabel,
        disabled: !pendingTradeMethod,
        onClick: () => {
          if (pendingTradeMethod) {
            selectPaymentType(pendingTradeMethod);
          }
        },
      },
      secondary: cancelAction,
    };
  }

  if (isNewFailed) {
    return { primary: actions.selectTradeMethod };
  }

  if (isTransfer) {
    if (isReceiptsStep) {
      return {
        primary: {
          ...submitReceiptAction,
          disabled: !hasPendingFile || isUploadingReceipt,
          isLoading: isUploadingReceipt,
        },
        secondary: cancelAction,
      };
    }
    return {
      primary: {
        ...actions.confirmPayment,
        onClick: confirmPaymentWithLoading,
      },
      secondary: cancelAction,
    };
  }

  if (isConfirmCard) {
    if (isTSBPSingleBankSelection) {
      return { primary: cancelAction };
    }
    return {
      primary: actions.confirmCard,
      secondary: cancelAction,
    };
  }

  if (isTraderConfirm && !actions.backToShop.hidden) {
    return {
      primary: {
        ...actions.backToShop,
        label: backToPersonalAccountLabel,
      },
    };
  }

  if (isFinalStatus) {
    if (hasPendingFile || isUploadingReceipt) {
      return {
        primary: {
          ...submitReceiptAction,
          disabled: !hasPendingFile || isUploadingReceipt,
          isLoading: isUploadingReceipt,
        },
      };
    }
    return {
      primary: {
        ...actions.backToShop,
        disabled: false,
        hidden: false,
      },
    };
  }

  return null;
}
